///
/// EarthBrowser
///
/// Derived storm insights shared by the info panel and hurricane browser:
/// intensity trend from the observed record, and the forecast track's
/// closest approach to a significant city from the local locations table
///
/// Created by Matt Giger on 9/6/2026
/// Copyright (c) 2026 Geodata Labs
///

import { LocationsTable } from "@/earthbrowser/dataset-distribution/locations/LocationsTable"
import { parseTrack, type StormProperties, type StormTrackPoint } from "./HurricanesTable"

const cEarthRadiusKm = 6371
const cDegToRad = Math.PI / 180

// Trend: wind change over the trailing ~12 hours of observations
const cTrendWindowMs = 12 * 3_600_000
const cTrendThresholdKt = 8

// Closest approach: how far beyond the storm bbox to look for cities, and
// what counts as a "significant" city before falling back to anything
const cCityBBoxPadDeg = 3
const cCityQueryLimit = 400
const cSignificantPop = 100_000
const cSegmentSubdivisions = 8

export type StormTrend = "intensifying" | "weakening" | "steady"

/**
 * Intensity trend from the observed track: compares the latest sustained
 * wind against the observation nearest 12 hours prior. Null when the record
 * is too short to say.
 */
export function stormTrend(observed: StormTrackPoint[]): StormTrend | null {
	const withWind = observed.filter(p => p.wind_kt !== null)
	if (withWind.length < 2) return null

	const latest = withWind[withWind.length - 1]
	const targetT = latest.t - cTrendWindowMs
	let reference = withWind[0]
	for (const point of withWind) {
		if (point.t > targetT) break
		reference = point
	}
	if (reference === latest) return null

	const delta = (latest.wind_kt ?? 0) - (reference.wind_kt ?? 0)
	if (delta >= cTrendThresholdKt) return "intensifying"
	if (delta <= -cTrendThresholdKt) return "weakening"
	return "steady"
}

export interface ClosestApproach {
	cityName: string
	cityLat: number
	cityLon: number
	/** Country or admin region for disambiguation */
	region: string
	distanceKm: number
	/** Compass direction of the storm from the city (e.g. "SW of Honolulu") */
	direction: string
	/** Time of closest approach, epoch milliseconds UTC */
	t: number
	/**
	 * When an arrival radius was given: the first forecast time the center
	 * comes within it (e.g. the 34-kt radius, so tropical-storm-force winds
	 * reach the city); undefined when the track never gets that close
	 */
	arrivalT?: number
}

/**
 * Find the significant city that the forecast track passes closest to, with
 * distance, direction and timing. Walks the forecast (including the current
 * position) against cities from the locations table inside the storm's
 * bounding box. Returns null when no forecast exists or no city is nearby.
 *
 * @param arrivalRadiusKm - when set, also report the first time the center
 * comes within this distance of the chosen city
 */
export async function closestApproach(storm: StormProperties, arrivalRadiusKm?: number): Promise<ClosestApproach | null> {
	const forecast = parseTrack(storm.forecast_track)
	if (forecast.length === 0) return null

	const cities = await LocationsTable.queryByBBox(
		storm.bbox_min_lon - cCityBBoxPadDeg,
		storm.bbox_min_lat - cCityBBoxPadDeg,
		storm.bbox_max_lon + cCityBBoxPadDeg,
		storm.bbox_max_lat + cCityBBoxPadDeg,
		cCityQueryLimit,
		undefined,
		"high"
	)
	if (cities.length === 0) return null

	// Densified forecast samples with interpolated times
	const samples: { lat: number, lon: number, t: number }[] = []
	samples.push({ lat: forecast[0].lat, lon: forecast[0].lon, t: forecast[0].t })
	for (let i = 1; i < forecast.length; i++) {
		const a = forecast[i - 1]
		const b = forecast[i]
		for (let s = 1; s <= cSegmentSubdivisions; s++) {
			const f = s / cSegmentSubdivisions
			samples.push({
				lat: a.lat + (b.lat - a.lat) * f,
				lon: a.lon + lonDelta(a.lon, b.lon) * f,
				t: a.t + (b.t - a.t) * f,
			})
		}
	}

	interface Candidate { city: typeof cities[number], distanceKm: number, sample: typeof samples[number] }
	let bestSignificant: Candidate | null = null
	let bestAny: Candidate | null = null

	for (const city of cities) {
		let minDist = Infinity
		let minSample = samples[0]
		for (const sample of samples) {
			const d = haversineKm(city.lat, city.lon, sample.lat, sample.lon)
			if (d < minDist) {
				minDist = d
				minSample = sample
			}
		}
		const candidate: Candidate = { city, distanceKm: minDist, sample: minSample }
		if (!bestAny || minDist < bestAny.distanceKm) bestAny = candidate
		if (city.pop >= cSignificantPop && (!bestSignificant || minDist < bestSignificant.distanceKm)) {
			bestSignificant = candidate
		}
	}

	const best = bestSignificant ?? bestAny
	if (!best) return null

	let arrivalT: number | undefined
	if (arrivalRadiusKm !== undefined && best.distanceKm <= arrivalRadiusKm) {
		for (const sample of samples) {
			if (haversineKm(best.city.lat, best.city.lon, sample.lat, sample.lon) <= arrivalRadiusKm) {
				arrivalT = sample.t
				break
			}
		}
	}

	return {
		cityName: best.city.name,
		cityLat: best.city.lat,
		cityLon: best.city.lon,
		region: best.city.admin1 || best.city.country,
		distanceKm: best.distanceKm,
		direction: cardinalFromBearing(bearingDeg(
			best.city.lat, best.city.lon, best.sample.lat, best.sample.lon
		)),
		t: best.sample.t,
		arrivalT,
	}
}

/** Longitude difference taking the short way around the dateline */
function lonDelta(a: number, b: number): number {
	let d = b - a
	if (d > 180) d -= 360
	if (d < -180) d += 360
	return d
}

function haversineKm(lat1: number, lon1: number, lat2: number, lon2: number): number {
	const dLat = (lat2 - lat1) * cDegToRad
	const dLon = lonDelta(lon1, lon2) * cDegToRad
	const a = Math.sin(dLat / 2) ** 2
		+ Math.cos(lat1 * cDegToRad) * Math.cos(lat2 * cDegToRad) * Math.sin(dLon / 2) ** 2
	return 2 * cEarthRadiusKm * Math.asin(Math.min(1, Math.sqrt(a)))
}

/** Initial great-circle bearing from point 1 toward point 2, degrees from north */
function bearingDeg(lat1: number, lon1: number, lat2: number, lon2: number): number {
	const f1 = lat1 * cDegToRad
	const f2 = lat2 * cDegToRad
	const dLon = lonDelta(lon1, lon2) * cDegToRad
	const y = Math.sin(dLon) * Math.cos(f2)
	const x = Math.cos(f1) * Math.sin(f2) - Math.sin(f1) * Math.cos(f2) * Math.cos(dLon)
	return ((Math.atan2(y, x) * 180 / Math.PI) + 360) % 360
}

function cardinalFromBearing(degrees: number): string {
	const directions = ["N", "NNE", "NE", "ENE", "E", "ESE", "SE", "SSE", "S", "SSW", "SW", "WSW", "W", "WNW", "NW", "NNW"]
	return directions[Math.round(degrees / 22.5) % 16]
}
